// The link from "will this fit on that drum?" to the page that answers it in 3D.
//
// Planning a delivery writes a drum type, a diameter and a length per unit, and
// the plan dialog can already say how many metres fit — but only as a number. The
// page that shows the winding, the layers and the drum in 3D is
// `/apps/standard/length-on-drum-standard`, and reaching it meant retyping the
// cable, picking the phase and picking the drum again from scratch.
//
// This builds that page's URL with the answer already in it.
//
// **The state goes in the QUERY STRING, not in router state.** The button opens a
// new tab — the planner keeps the order open beside it — and `navigate(..., {
// state })` does not survive `window.open`/`target="_blank"`. A URL also means
// the tab can be bookmarked, re-loaded and sent to somebody else, which a piece
// of in-memory router state cannot.
//
// Keep in step with `pages/standard/StandardDrumLength.js`, which reads these
// names. `tests/drumLengthLink.test.js` pins both halves.

export const DRUM_LENGTH_PATH = '/apps/standard/length-on-drum-standard';

/** Query parameter names — one place, because two files read and write them. */
export const PARAMS = {
  name: 'name',
  cores: 'cores',
  section: 'section',
  greenYellowSection: 'gy',
  color: 'color',
  phase: 'phase',
  drumMaterial: 'drumMaterial',
  drumDiameter: 'drumDiameter',
  length: 'length',
};

/**
 * `phase=final` means "whatever layer the customer receives".
 *
 * From sales there is only one interesting diameter — the finished cable — and
 * which phase that is differs per product: an armoured cable ends at the outer
 * sheath, an FY ends at the insulation. Naming the INTENT rather than a phase
 * means the answer is worked out on the page, from the standard, by the same
 * rule `getCableOuterDiameter` uses.
 */
export const FINAL_PHASE = 'final';

const has = v => v !== undefined && v !== null && String(v).trim() !== '';

/**
 * The cable's name, whatever the caller's object calls it.
 *
 * Sales lines say `cableName`; everything on the production side says `name`.
 * Both of this link's call sites are sales screens, both passed their own line
 * object straight in, and `product.name` was undefined on both — so every URL
 * went out without the one parameter the page needs:
 *
 *   ?cores=3&section=2.5&color=GREY&phase=final&drumMaterial=wood&...
 *
 * `readDrumLengthParams` returns null when the URL carries no cable, which is
 * the correct behaviour for somebody opening the page from the menu — so the
 * link opened the page blank and silent. Read here rather than fixed at each
 * call site: a third screen would get it wrong the same way.
 */
const cableNameOf = product =>
  product?.name ?? product?.cableName ?? product?.productLabel ?? null;

/**
 * @param product  { name | cableName, numberOfConductors, section, color,
 *                   hasMixedConductorSections, greenYellowSection }
 * @param drum     { drumMaterial, drumDiameter } — optional, preselects it
 * @param length   metres per unit, optional, prefills "requested length"
 * @param phase    FINAL_PHASE (default) or a phase name like 'exterior_sheat'
 */
export function drumLengthStandardUrl({
  product = {},
  drumMaterial,
  drumDiameter,
  length,
  phase = FINAL_PHASE,
} = {}) {
  const q = new URLSearchParams();

  const name = cableNameOf(product);
  if (has(name)) q.set(PARAMS.name, String(name).trim().toUpperCase());
  if (has(product.numberOfConductors)) q.set(PARAMS.cores, String(product.numberOfConductors));
  if (has(product.section)) q.set(PARAMS.section, String(product.section));
  if (has(product.color)) q.set(PARAMS.color, String(product.color).trim().toUpperCase());

  // The reduced earth is part of the cable's identity, not a detail: a
  // 3x25+16 is a different standard from a 3x25 and winds differently. Carried
  // only when the product actually has one — `hasMixedConductorSections` is
  // then implied by the parameter's presence, so there is no second flag to
  // disagree with it.
  if (product.hasMixedConductorSections && has(product.greenYellowSection)) {
    q.set(PARAMS.greenYellowSection, String(product.greenYellowSection));
  }

  if (has(phase)) q.set(PARAMS.phase, String(phase));
  if (has(drumMaterial)) q.set(PARAMS.drumMaterial, String(drumMaterial).trim().toLowerCase());
  if (has(drumDiameter)) q.set(PARAMS.drumDiameter, String(drumDiameter));
  if (has(length) && Number(length) > 0) q.set(PARAMS.length, String(Number(length)));

  const s = q.toString();
  return s ? `${DRUM_LENGTH_PATH}?${s}` : DRUM_LENGTH_PATH;
}

/**
 * The other end: what the page should start with, read off its own URL.
 *
 * Returns null when the URL carries no cable, which is the ordinary case of
 * somebody opening the page from the menu — it must then behave exactly as it
 * always has.
 */
export function readDrumLengthParams(search) {
  const q = new URLSearchParams(search || '');
  const name = (q.get(PARAMS.name) || '').trim();
  if (!name) return null;

  const gy = q.get(PARAMS.greenYellowSection);
  // `Number(null)` and `Number('')` are both 0, so a parameter that is simply
  // ABSENT would read as "a drum of diameter zero" — and a zero diameter is a
  // capacity computed from nothing. Absent has to stay absent.
  const num = v => {
    if (v === null || v === undefined || String(v).trim() === '') return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  };

  return {
    product: {
      name: name.toUpperCase(),
      numberOfConductors: q.get(PARAMS.cores) ?? '',
      section: q.get(PARAMS.section) ?? '',
      color: (q.get(PARAMS.color) || '').toUpperCase(),
      // Presence of `gy` IS the flag. One source, so the two cannot disagree.
      hasMixedConductorSections: gy != null && gy !== '' && num(gy) > 0,
      greenYellowSection: gy != null && gy !== '' ? gy : '',
    },
    phase: q.get(PARAMS.phase) || FINAL_PHASE,
    drumMaterial: (q.get(PARAMS.drumMaterial) || '').toLowerCase() || null,
    drumDiameter: num(q.get(PARAMS.drumDiameter)),
    length: num(q.get(PARAMS.length)),
  };
}

/**
 * Which phase option is the finished cable.
 *
 * Same precedence as `getCableOuterDiameter` in helpers/drumCapacity.js — the
 * outer sheath if there is one, otherwise the outermost layer that has a
 * diameter — so the length the plan dialog quoted and the length this page
 * computes are about the same layer. They disagreeing would be worse than not
 * linking the two pages at all.
 *
 * @param options as built by StandardDrumLength's `extractPhaseOptions`
 */
export function pickFinalPhaseOption(options = []) {
  const usable = (options || []).filter(o => Number(o?.diameter) > 0);
  if (!usable.length) return null;

  const outer = usable.filter(o => o.name === 'exterior_sheat');
  if (outer.length) {
    // One option per unique diameter, so a sheath with several is the widest.
    return outer.reduce((a, b) => (Number(b.diameter) > Number(a.diameter) ? b : a));
  }

  // No outer sheath: the last layer that has a diameter, which for FY and MYF is
  // the insulated conductor they are sold as.
  return usable[usable.length - 1];
}

/** A named phase, with the final-cable rule as the fallback. */
export function pickPhaseOption(options = [], phase = FINAL_PHASE) {
  if (!phase || phase === FINAL_PHASE) return pickFinalPhaseOption(options);
  const named = (options || []).filter(o => o?.name === phase && Number(o.diameter) > 0);
  if (!named.length) return pickFinalPhaseOption(options);
  return named.reduce((a, b) => (Number(b.diameter) > Number(a.diameter) ? b : a));
}

export default drumLengthStandardUrl;
